import { useCallback, useEffect, useRef, useState } from "react";
import { loadCoachSessionSettings } from "../utils/liveTrainingSettings";

export const EXERCISE_INSTRUCTION_SOURCE = "exercise_instruction";

/**
 * Coach-controlled start/stop for exercise-instruction capture on a table camera phone.
 * Relays via start/stop_table_video_recording with source=exercise_instruction (no player target).
 */
export function useCoachInstructionRecording({
  socket,
  teId,
  tsId,
  coachUserId,
}) {
  const [status, setStatus] = useState("idle"); // idle | starting | recording | stopping | error
  const [error, setError] = useState(null);
  const [tableNumber, setTableNumber] = useState(null);
  const [captureId, setCaptureId] = useState(null);
  const pendingActionRef = useRef(null); // "start" | "stop" | null
  const tableNumberRef = useRef(null);

  useEffect(() => {
    tableNumberRef.current = tableNumber;
  }, [tableNumber]);

  useEffect(() => {
    if (!socket) return undefined;

    const onAck = (ack = {}) => {
      const action = String(ack.action || "");
      const pending = pendingActionRef.current;
      if (action === "start_video" && pending === "start") {
        pendingActionRef.current = null;
        if (ack.ok === false) {
          setStatus("error");
          setCaptureId(null);
          setError(
            ack.code === "CONSENT_REQUIRED"
              ? "Video consent required"
              : ack.code === "missing_fields"
                ? "Missing table or exercise"
                : ack.code || "Could not start recording"
          );
          return;
        }
        // Session-room broadcast reports delivered=null (includes coach).
        // Only fail when the relay targeted seated phones and none were online.
        if (
          ack.broadcast !== true &&
          ack.delivered != null &&
          Number(ack.delivered) === 0
        ) {
          setStatus("error");
          setCaptureId(ack.capture_id || null);
          setError(
            "No table camera phone online for that table. Enable table phone on a device at the table."
          );
          return;
        }
        setCaptureId(ack.capture_id || null);
        setError(null);
        setStatus("recording");
        return;
      }
      if (action === "stop_video" && pending === "stop") {
        pendingActionRef.current = null;
        if (ack.ok === false) {
          setStatus("error");
          setError(ack.code || "Could not stop recording");
          return;
        }
        setCaptureId(null);
        setError(null);
        setStatus("idle");
      }
    };

    socket.on("coachTableRecordingAck", onAck);
    return () => {
      socket.off("coachTableRecordingAck", onAck);
    };
  }, [socket]);

  // Reset when exercise changes.
  useEffect(() => {
    pendingActionRef.current = null;
    setStatus("idle");
    setError(null);
    setCaptureId(null);
    setTableNumber(null);
  }, [teId]);

  const start = useCallback(
    (selectedTableNumber) => {
      const tn = Number(selectedTableNumber);
      if (!socket || !teId || !coachUserId || !Number.isFinite(tn) || tn <= 0) {
        setStatus("error");
        setError("Select a table to record instructions");
        return;
      }
      if (status === "recording" || status === "starting") return;

      setTableNumber(tn);
      tableNumberRef.current = tn;
      setError(null);
      setStatus("starting");
      pendingActionRef.current = "start";

      const fps = loadCoachSessionSettings(tsId).recommendedRecordingFps;
      socket.emit("start_table_video_recording", {
        te_id: teId,
        ts_id: tsId,
        coach_id: coachUserId,
        table_number: tn,
        source: EXERCISE_INSTRUCTION_SOURCE,
        fps,
      });
    },
    [socket, teId, tsId, coachUserId, status]
  );

  const stop = useCallback(() => {
    const tn = Number(tableNumberRef.current);
    if (!socket || !teId || !coachUserId || !Number.isFinite(tn) || tn <= 0) {
      return;
    }
    if (status !== "recording" && status !== "starting") return;

    setError(null);
    setStatus("stopping");
    pendingActionRef.current = "stop";

    socket.emit("stop_table_video_recording", {
      te_id: teId,
      ts_id: tsId,
      coach_id: coachUserId,
      table_number: tn,
      source: EXERCISE_INSTRUCTION_SOURCE,
    });
  }, [socket, teId, tsId, coachUserId, status]);

  const resetError = useCallback(() => {
    if (status === "error") {
      setStatus("idle");
      setError(null);
    }
  }, [status]);

  return {
    status,
    error,
    tableNumber,
    captureId,
    recording: status === "recording" || status === "starting",
    start,
    stop,
    resetError,
    setTableNumber,
  };
}
